import React, { useState } from "react";
import "./DynamicFormRenderer.scss";
import {
  Box,
  Typography,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Card,
  CardContent,
  Stack,
  Divider,
  Collapse,
} from "@mui/material";

import IconButtons from "../../../components/IconButtons/IconButtons";
import { font_13_icon } from "../../../utils/utils";
import {
  delete_active_icon,
  down_arrow_icon,
  up_arrow_icon,
  move_down_icon,
  move_up_icon,
  edit_active_icon,
  delete_disabled_icon,
  edit_disabled_icon,
  move_down_disable_icon,
  move_up_disable_icon,
} from "../../../constants/icons";
import { useSelector } from "react-redux";

const isScaleEnabled = (field) => {
  if (field?.__meta?.scaleCheckBoxEnabled) return true;

  return (
    field.type === "NUMERIC_TEXT" &&
    Array.isArray(field.options) &&
    field.options.includes("scaleCheckBoxEnabled")
  );
};

const DynamicFormRenderer = ({
  formFields = [],
  onEdit,
  onDelete,
  onMoveUp,
  onMoveDown,
  disabled,
  selectedFormType
}) => {
  const utilityConstants = useSelector(
    (state) => state?.utility?.constants
  );

  const [expandedId, setExpandedId] = useState(null);

  const toggleExpand = (id) => {
    setExpandedId((prevId) => (prevId === id ? null : id));
  };
  const getFieldLabelById = (id) => {
    const targetField = formFields.find(f => (f.id === id || f.tempId === id));
    return targetField ? targetField.label : id;
  };
  if (!Array.isArray(formFields)) return null;

  const getScaleLegend = (formType) => {
    const performanceTypes = [
      "CLASSROOM_ASSESSMENT_PERFORMANCE",
      "SHADOW_PROJECT_ASSESSMENT_PERFORMANCE",
    ];
    return performanceTypes.includes(formType)
      ? "1 - Poor*, 2 - Below Average*, 3 - Average*, 4 - Good, 5 - Excellent"
      : "1 - Never*, 2 - Rarely*, 3 - Not sure*, 4 - Sometimes, 5 - Always";
  };

  const getLabelFromKey = (listName, apiKey) => {
    if (!utilityConstants || !utilityConstants[listName]) return apiKey;
    const foundItem = utilityConstants[listName].find(item => item.key === apiKey);
    return foundItem ? foundItem.value : apiKey;
  };
  const normalizeOptions = (options = []) => {
    return options.map((opt, idx) => {
      if (typeof opt === "string") {
        return { label: opt || "(empty)", value: opt || `empty_${idx}` };
      } else if (typeof opt === "object" && opt !== null) {
        return { label: opt.label || "(empty)", value: opt.value || `empty_${idx}` };
      } else {
        return { label: "(empty)", value: `empty_${idx}` };
      }
    });
  };

  const renderFieldPreview = (field) => {
    const options = normalizeOptions(field.options);
    const normalizedDefaultValue = Array.isArray(field.defaultValue)
      ? field.defaultValue[0]
      : field.defaultValue;

    const valueToRender = normalizedDefaultValue ?? "";

    switch (field.type) {
      case "TEXT":
        return (
          <TextField
            className="text-input"
            size="small"
            color="secondary"
            fullWidth
            disabled
            type="text"
            placeholder={field.placeholder}
            value={valueToRender}
          />
        );

      case "AVERAGE_SCORE":
        return (
          <TextField
            className="text-input"
            size="small"
            color="secondary"
            fullWidth
            disabled
            type="text"
            value={valueToRender}
            sx={{ bgcolor: "rgba(0, 0, 0, 0.04)" }}
          />
        );

      case "NUMERIC_TEXT":
        if (isScaleEnabled(field)) {
          const min = Number(field.minValue ?? 1);
          const max = Number(field.maxValue ?? 5);
          const scaleValues = [];
          for (let i = min; i <= max; i++) {
            scaleValues.push(String(i));
          }

          return (
            <Box>
              <RadioGroup row value={String(valueToRender)} sx={{ gap: 1, flexWrap: "wrap" }}>
                {scaleValues.map((val) => (
                  <FormControlLabel
                    key={val}
                    value={val}
                    control={<Radio size="small" color="secondary" disabled />}
                    label={val}
                    className={String(valueToRender) === val ? "selected-scale-item" : ""}
                  />
                ))}
              </RadioGroup>
            </Box>
          );
        }
        return (
          <TextField
            className="text-input"
            size="small"
            color="secondary"
            fullWidth
            disabled
            type="number"
            placeholder={field.placeholder}
            value={valueToRender}
          />
        );

      case "TEXTAREA":
        return (
          <TextField
            className="text-input"
            size="small"
            fullWidth
            multiline
            rows={3}
            disabled
            color="secondary"
            placeholder={field.placeholder}
            value={valueToRender}
          />
        );

      case "RADIO_OPTIONS":
        return (
          <RadioGroup className="interview-options" value={valueToRender}>
            {options.map((opt) => (
              <FormControlLabel
                key={opt.value}
                value={opt.value}
                control={<Radio size="small" color="secondary" disabled />}
                label={opt.label}
              />
            ))}
          </RadioGroup>
        );

      case "CHECKBOX_OPTIONS":
      case "PERFORMANCE_ASSESSED_BY":
      case "PROJECT_NAME": {
        // For checkboxes, even if we only show one default, we compare against an array logic
        const checkedValue = String(valueToRender);
        return (
          <Box>
            {options.map((opt) => (
              <FormControlLabel
                key={opt.value}
                control={
                  <Checkbox
                    className="text-input"
                    size="small"
                    color="secondary"
                    disabled
                    // Compare as strings to avoid type mismatch (e.g., 1 vs "1")
                    checked={String(opt.value) === checkedValue}
                  />
                }
                label={opt.label}
              />
            ))}
          </Box>
        );
      }

      case "DROPDOWN_SINGLE_SELECT":
      case "DROPDOWN_MULTI_SELECT":
        return (
          <TextField
            size="small"
            className="text-input"
            color="secondary"
            fullWidth
            select
            disabled
            // Crucial: Use SelectProps native for <option> tags
            SelectProps={{ native: true }}
            value={valueToRender}
          >
            <option value="" disabled></option>
            {options.map((opt) => (
              <option key={String(opt.value)} value={String(opt.value)}>
                {opt.label}
              </option>
            ))}
          </TextField>
        );

      default:
        return <Typography>Unsupported Field</Typography>;
    }
  };

  const isPresent = (v) => {
    if (v === null || v === undefined) return false;
    if (typeof v === "string" && v.trim() === "") return false;
    return true;
  };

  const hasItems = (arr) => Array.isArray(arr) && arr.length > 0;

  return (
    <Box>
      <Stack spacing={2} sx={{ mt: 2 }}>
        {formFields.map((field, idx) => {
          const key = field.id ?? field.tempId ?? `${idx}`;
          const isFirst = idx === 0;
          const isLast = idx === formFields.length - 1;
          const idOrTemp = field.id ?? field.tempId;
          const isExpanded = expandedId === idOrTemp;

          const hasDependency =
            hasItems(field.dependentOnIds) ||
            isPresent(field.dependentValue) ||
            isPresent(field.dependentCondition);

          const showRange = (field.minValue ?? "") !== "" || (field.maxValue ?? "") !== "";

          return (
            <Card key={key} className="df-card">
              <CardContent className="df-card-content">
                <Box className="df-field-header">
                  <Box>
                    <Typography className="df-label">
                      {field.label} {field.required ? "*" : ""}
                    </Typography>
                    <Typography className="df-description">
                      {field.description ? field.description : ""}
                      {isScaleEnabled(field) && (
                        <Typography
                          component="span"
                          variant="caption"
                          display="inline"
                          sx={{
                            ml: 1,
                            fontStyle: 'italic',
                            color: 'text.secondary'
                          }}
                        >
                          {`( ${getScaleLegend(selectedFormType)} )`}
                        </Typography>
                      )}
                    </Typography>
                  </Box>
                  <Box className="df-actions-inline">
                    {!disabled && (
                      <>
                        <IconButtons
                          height={font_13_icon}
                          width={font_13_icon}
                          image={move_up_icon}
                          disabledImage={move_up_disable_icon}
                          handleClick={() => onMoveUp(idOrTemp)}
                          tooltip="Move Up"
                          disabled={isFirst || disabled}
                          disabledTooltip={"Reorder is disabled."}
                        />

                        <IconButtons
                          height={font_13_icon}
                          width={font_13_icon}
                          image={move_down_icon}
                          disabledImage={move_down_disable_icon}
                          handleClick={() => onMoveDown(idOrTemp)}
                          tooltip="Move Down"
                          disabled={isLast || disabled}
                          disabledTooltip={"Reorder is disabled."}
                        />

                        <IconButtons
                          height={font_13_icon}
                          width={font_13_icon}
                          image={edit_active_icon}
                          disabled={disabled}
                          disabledImage={edit_disabled_icon}
                          handleClick={() => onEdit(field)}
                          tooltip="Edit Field"
                          disabledTooltip={"Editing is disabled."}
                        />

                        <IconButtons
                          height={font_13_icon}
                          width={font_13_icon}
                          image={delete_active_icon}
                          disabledImage={delete_disabled_icon}
                          disabled={disabled}
                          handleClick={() => onDelete(idOrTemp)}
                          tooltip="Delete Field"
                          disabledTooltip={"Deletion is not allowed for this field."}
                        />
                      </>
                    )}
                    <IconButtons
                      height={font_13_icon}
                      width={font_13_icon}
                      image={isExpanded ? up_arrow_icon : down_arrow_icon}
                      handleClick={() => toggleExpand(idOrTemp)}
                      tooltip={isExpanded ? "Collapse" : "Expand"}
                    />
                  </Box>
                </Box>

                <Box className="df-field-wrapper">{renderFieldPreview(field)}</Box>
                <Collapse in={isExpanded}>
                  <Divider sx={{ my: 1 }} />

                  <Box className="df-expanded-details">
                    <Typography className="df-detail-row" variant="font_12_bold">
                      <strong>Type:</strong> {getLabelFromKey('formInputType', field.type)}
                    </Typography>

                    {showRange && (
                      <Typography className="df-detail-row" variant="font_12_bold">
                        <strong>Range:</strong> {field.minValue ?? "—"} to {field.maxValue ?? "—"}
                      </Typography>
                    )}

                    {hasDependency && (
                      <Typography className="df-detail-row" variant="font_12_bold">
                        <strong>Dependencies:</strong>{" "}
                        {hasItems(field.dependentOnIds) &&
                          field.dependentOnIds.map(id => getFieldLabelById(id)).join(", ")
                        }{"  "}
                        {isPresent(field.dependentCondition) &&
                          getLabelFromKey("dependentCondition", field.dependentCondition)
                        }{"  "}
                        {isPresent(field.dependentValue) &&
                          String(field.dependentValue)
                        }
                      </Typography>
                    )}

                  </Box>
                </Collapse>
              </CardContent>
            </Card>
          );
        })}
      </Stack>
    </Box>
  );
};

export default DynamicFormRenderer;